import React from 'react';
import {Minus, Plus} from 'lucide-react';
import {cn} from '../lib/cn';
import {ErrorMessage} from '../error-message';

export enum CounterSize {
  S = 'small',
  L = 'large',
}

export type CounterProps = {
  value: number;
  onChange?: (value: number) => void;
  disabled?: boolean;
  readOnly?: boolean;
  error?: string | boolean;
  minNumber?: number;
  maxNumber?: number;
  size?: CounterSize;
  className?: string;
};

const getIconSize = (size?: CounterSize): number => {
  if (size === CounterSize.S) {
    return 16;
  }
  return 24;
};

const getSizeClasses = (size?: CounterSize) => {
  if (size === CounterSize.S) {
    return {
      button: 'w-6 h-6 rounded-lg',
      container: 'gap-4',
      number: 'text-sm leading-6',
    };
  }
  return {
    button: 'w-8 h-8 rounded-[10px]',
    container: 'gap-6',
    number: 'text-xl',
  };
};

export const Counter = ({
  value,
  error,
  onChange,
  disabled,
  readOnly,
  minNumber = 0,
  maxNumber = 20,
  size,
  className,
}: CounterProps) => {
  const sizeClasses = getSizeClasses(size);
  const iconSize = getIconSize(size);
  const hasError = !!error;

  const handleDecrease = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    const checkedValue = value ?? 0;
    if (checkedValue < 1) return;
    if (!disabled) {
      onChange?.(checkedValue - 1);
    }
  };

  const handleIncrease = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    const checkedValue = value ?? 0;
    if (!disabled) {
      onChange?.(checkedValue + 1);
    }
  };

  return (
    <div
      className={cn(
        'counter flex items-center flex-shrink-0 rounded-[20px] w-[136px] h-full relative',
        sizeClasses.container,
        className,
      )}
    >
      {!readOnly && (
        <button
          type="button"
          disabled={disabled || value === minNumber}
          onClick={handleDecrease}
          aria-label="Decrease"
          className={cn(
            'counter__decrease-button counter__button',
            'flex items-center justify-center flex-shrink-0 p-0 relative',
            'border transition-opacity',
            'bg-transparent',
            hasError
              ? 'border-[var(--counter-error-border)]'
              : 'border-[var(--counter-button-border)]',
            'disabled:pointer-events-none disabled:opacity-70',
            sizeClasses.button,
          )}
        >
          <Minus size={iconSize} className="text-[var(--counter-icon-color)]" />
        </button>
      )}
      <span
        className={cn(
          'counter__value flex-grow text-center font-bold select-none',
          hasError ? 'text-[var(--counter-error-text)]' : 'text-[var(--counter-text)]',
          sizeClasses.number,
        )}
      >
        {value}
      </span>
      {!readOnly && (
        <button
          type="button"
          disabled={disabled || value === maxNumber}
          onClick={handleIncrease}
          aria-label="Increase"
          className={cn(
            'counter__increase-button counter__button',
            'flex items-center justify-center flex-shrink-0 p-0 relative',
            'border transition-opacity',
            'bg-transparent',
            hasError
              ? 'border-[var(--counter-error-border)]'
              : 'border-[var(--counter-button-border)]',
            'disabled:pointer-events-none disabled:opacity-70',
            sizeClasses.button,
          )}
        >
          <Plus size={iconSize} className="text-[var(--counter-icon-color)]" />
        </button>
      )}
      {error && typeof error === 'string' && <ErrorMessage>{error}</ErrorMessage>}
    </div>
  );
};
